import classNames from 'classnames';
import * as _ from 'lodash-es';
import React from 'react';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated';
import addTestId from '../../utils/addTestId';
import Link from '../../utils/router/Link';
import postPath from '../../utils/url/postPath';
import type { TOCHeader } from './ConnectedTableOfContents';
import * as styles from './TableOfContents.module.scss';

const POST_FRAGMENT = gql(`
  fragment TableOfContents_post on PostFields {
    id
    ...postPath_post
  }
`);

interface TableOfContentsProps {
  post?: DocumentType<typeof POST_FRAGMENT>;
  headers: TOCHeader[];
  activeH1?: string | null;
  activeH2?: string | null;
  activeH3?: string | null;
  small?: boolean;
}

const TableOfContents = ({
  post,
  headers,
  activeH1 = null,
  activeH2 = null,
  activeH3 = null,
  small,
}: TableOfContentsProps) => {
  const minLevel = Math.min(...headers.map(({ level }) => level));

  return (
    <ul
      className={classNames(styles.toc, { [styles.small]: small })}
      {...addTestId('tableOfContents')}
    >
      {headers.map(({ text, level, id }, i) => {
        if (_.isEmpty(text)) {
          return null;
        }
        // If there are no h1s, make h2s look like h1s
        const effectiveLevel = Math.min(level, 3) - minLevel + 1;
        let className = styles[`level${effectiveLevel}`];
        if ([activeH1, activeH2, activeH3].includes(id)) {
          className += ` ${styles.active}`;
        }

        // We use the index as a react key because header text doesn't have to be unique
        const key = `${i}-${id}`;
        const anchor = encodeURIComponent(id);

        return (
          <li className={className} key={key}>
            <Link
              to={post ? postPath(post, { anchor }) : `#${anchor}`}
              title={text}
            >
              {text}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default TableOfContents;
